// ═══════════════════════════════════════════════════════════════════
// boardMotion.js — THE VERBS OF THE DUEL · H24 · Terror TCG
// The moments that are not a card. Transcend, Ascend, Deviance,
// Fracture, Fall, Ruin, Graveshift, the Response Window, and the end
// of a Solrend.
// ═══════════════════════════════════════════════════════════════════
//
// caP'n (Aug 3): "all the effects, all the interactions, everything
// that makes sense."
//
// ── THE GAP THIS FILLS ─────────────────────────────────────────────
// The other four score files answer "what does this CARD do." This one
// answers "what does the GAME do," and it is a genuinely different
// list. Nothing in cards.js is called Transcend. There is no card named
// Fracture. But a being crossing from Escling to Beb is one of the most
// important events in a Solrend, it happens several times a match, and
// until now the board's entire statement about it was that a number
// changed.
//
// These are also the scores a player sees MOST. A given Impera might
// come up twice a night; an Ascend happens every few turns of every
// match ever played. So the budget rule here is the inverse of the
// U'hltis': the more often a thing happens, the shorter and quieter its
// score must be. Nothing in this file is allowed to be beautiful at the
// cost of being tiresome on the four-hundredth viewing.
//
//   seen once a match      ~1000–1600ms   Supreme, Graveshift, the end
//   seen a few times       ~700–900ms     Ascend, Deviance, Ruin
//   seen constantly        ~300–520ms     Fracture, Khao, the Window
//
// ── KEYED BY STRING, NOT BY CARD ───────────────────────────────────
// These resolve through `boardScore(key)` rather than `scoreFor(card,
// channel)` because there is no card to hand it. The renderer's
// transition detector already sees every one of these state changes;
// what it lacked was something to say about them.
// ═══════════════════════════════════════════════════════════════════

import {
  P, score, toll, wound, SHAKE, SURGE,
  shock, motes, pillar, spiral, wave, bloom,
  stream, glyph, grave, rune, veil, shatter,
  chain, coin, crack, vortex, embers, halo,
  star, rift, sever
} from "./motionKit";

const S = { to: "self" };

export const BOARD = {

// ═══════════════════════════════════════════════════════════════════
// THE LADDER — Transcend · Ascend · Deviance · Supreme
// ═══════════════════════════════════════════════════════════════════
// Terror's progression is Escling → Beb (L1) → Champion (L2) → Lord
// (L3), and every step is paid for. THE THREE-LEVEL LAW means there is
// no fourth rung, ever — so the Lord step has to feel like a ceiling
// being reached, not like another increment.
//
// The shared grammar across all of them: the being's own light rises
// (embers), the rung is inscribed (rune), and the new rank is stamped
// (star or halo). What escalates is how much sky is involved.

  // ── TRANSCEND ─────────────────────────────────────────────────────
  // Escling → Lvl 1. "Primordial essence waiting to be Transcended"
  // stops waiting. The one step in the ladder that is a BECOMING rather
  // than a promotion — the Escling had not decided what it was yet, and
  // now it has. So this is the only rung that uses the vortex: the
  // essence is gathered before it is shaped.
  transcend: score("Transcend", [
    vortex(0, P.bone, { ...S, ms: 480, r: 0.9, n: 34, spin: 4.6, size: 0.7 }),
    embers(200, P.vellum, { ...S, ms: 520, n: 22, r: 0.55, size: 0.07, size2: 1.5 }),
    rune(320, P.vellum, { ...S, ms: 520, r: 0.95, n: 6, spin: 0.6 }),
    star(560, P.starWhite, { ...S, ms: 360, r: 1.2, size: 0.045 }),
    ...toll(P.vellum, 580, 1, "self"),
  ], 940),

  // ── ASCEND ────────────────────────────────────────────────────────
  // Lvl 1 → 2, and 2 → 3. A promotion along the being's own line: same
  // school, same Ilk, one rung up. Quick and clean, because it happens
  // constantly. The rune has more ticks than Transcend's and that is
  // the entire difference — the ladder is being climbed, not entered.
  ascend: score("Ascend", [
    embers(0, P.goldPale, { ...S, ms: 480, n: 20, r: 0.5, size: 0.07, size2: 1.4 }),
    rune(140, P.gold, { ...S, ms: 520, r: 1.0, n: 10, spin: 0.7 }),
    star(420, P.judgeGold, { ...S, ms: 360, r: 1.4, size: 0.05 }),
    ...toll(P.gold, 440, 2, "self"),
  ], 860),

  // ── ASCEND · TO LORD ──────────────────────────────────────────────
  // The top of the ladder and the last one there will ever be. This is
  // the ONLY board verb that gets a pillar, and it gets it for exactly
  // the same reason a Lord's summon does: at Lord weight the world
  // opens rather than the being merely improving. A player who has
  // ascended twenty Champions and then sees this knows immediately that
  // something different happened.
  ascend_lord: score("Ascend · Lord", [
    pillar(0, P.goldPale, { ...S, ms: 660, r: 5.4, size: 0.46, spin: 0.4 }),
    embers(120, P.emberGold, { ...S, ms: 600, n: 30, r: 0.75, size: 0.075, size2: 1.9 }),
    rune(260, P.gold, { ...S, ms: 620, r: 1.3, n: 14, spin: 0.6 }),
    halo(520, P.goldPale, { ...S, ms: 680, r: 1.05, size: 0.84, spin: 1.2 }),
    ...toll(P.gold, 560, 3, "self"),
  ], 1280),

  // ── DEVIANCE ──────────────────────────────────────────────────────
  // Off-line ascension: school matches, Ilk does NOT. It is permanent,
  // it cannot be undone, and it permanently blocks the being from ever
  // taking a U'hlti or an Affinity. A Deviant being has traded its
  // ceiling for a shortcut.
  //
  // So this must never look like a reward, and it is the one score in
  // this file with a sour note in it: the rune runs `tone:"close"` —
  // the ladder shutting rather than opening — and the light is the
  // wrong colour for the rung. Nothing here is bright.
  deviance: score("Deviance", [
    embers(0, P.violetDeep, { ...S, ms: 520, n: 22, r: 0.55, size: 0.07, size2: 1.4 }),
    rune(160, P.amethyst, { ...S, ms: 600, r: 1.1, n: 8, spin: 0.9, tone: "close" }),
    crack(380, P.shadow, { ...S, ms: 540, n: 5, r: 1.5 }),
    ...toll(P.violetDeep, 440, 2, "self"),
  ], 980),

  // ── SUPREME ───────────────────────────────────────────────────────
  // A Natural Lvl 3 carrying BOTH a U'hlti and an Affinity. The single
  // highest state a being can reach in Terror, gated behind the entire
  // attachment ladder, and it is what four of the eight Prophecies are
  // counting when they check for victory.
  //
  // It happens at most a handful of times in a match and often zero, so
  // it is allowed to be the longest score in this file. Two halos on
  // different axes — every other crowned state in the game wears one —
  // and the only double pillar outside a U'hlti.
  supreme: score("A Supreme Being", [
    pillar(0, P.goldPale, { ...S, ms: 720, r: 6.2, size: 0.5, spin: 0.5 }),
    pillar(160, P.starWhite, { ...S, ms: 700, r: 6.2, size: 0.3, spin: -0.7 }),
    spiral(220, P.judgeGold, { ...S, ms: 700, tone: "up", spin: 3, r: 0.85, size: 3.2 }),
    halo(560, P.goldPale, { ...S, ms: 820, r: 1.25, size: 0.9, spin: 1.1 }),
    halo(700, P.starWhite, { ...S, ms: 800, r: 0.95, size: 1.05, spin: -1.6 }),
    glyph(760, P.judgeGold, { ...S, ms: 640, tone: "◈", size: 54, r: 0.85 }),
    ...toll(P.goldPale, 820, 3, "self"),
  ], 1720, { surge: SURGE.full(P.goldPale, 560) }),

// ═══════════════════════════════════════════════════════════════════
// HARM — Fracture · Fall · Ruin
// ═══════════════════════════════════════════════════════════════════
// Three distinct bad things, routinely confused by players, and the
// lexicon is precise about them:
//
//   FRACTURED  effective Lyf 0 — STILL STANDING, and vulnerable
//   FALLEN     descended a rung — bypassable
//   RUINED     gone to the Necropolis
//
// They must therefore look nothing like each other. This is the same
// discipline as Nihil vs Omnia and it matters just as much: a player
// who cannot tell at a glance whether their Champion is fractured or
// fallen is a player who misplays their next attack.

  // ── FRACTURE ──────────────────────────────────────────────────────
  // Lyf 0 but the being is still there. Nothing dies, nothing leaves —
  // the card simply becomes glass. So: a crack UNDER it and no debris
  // whatsoever. The absence of a shatter is the whole message, and R28
  // means this often un-happens at end of turn, so it must never read
  // as final.
  //
  // 380ms. The shortest score in the game. It happens constantly.
  fracture: score("Fractured", [
    crack(0, P.animaRed, { ...S, ms: 380, n: 5, r: 1.1 }),
    shock(60, P.crimson, { ...S, ms: 320, size: 0.14, r: 0.95, tone: 0.6 }),
  ], 380),

  // ── FALL ──────────────────────────────────────────────────────────
  // The being descends a rung — its lower level re-enters its own Soul.
  // Not a death and not a wound: a demotion. So the motion is DOWNWARD
  // and inward, the exact inverse of Ascend's rising embers, and it
  // reuses Ascend's rune running closed. Watching the two back to back
  // should read as the same door opening and shutting.
  fall: score("Fallen", [
    rune(0, P.bone, { ...S, ms: 480, r: 1.0, n: 10, spin: 0.5, tone: "close" }),
    motes(120, P.vellum, { ...S, ms: 460, n: 20, r: 1.2, size: 0.065, tone: "in" }),
    shock(340, P.bone, { ...S, ms: 400, size: 0.16, r: 1.3, tone: 0.5 }),
  ], 720),

  // ── RUIN ──────────────────────────────────────────────────────────
  // Gone. The canonical destruction pair — crack, then shatter — which
  // by now the player has already learned from every Omnia Tome in the
  // game. This is the generic version, fired whenever anything reaches
  // the Necropolis without a card of its own to say it.
  ruin: score("Ruined", [
    crack(0, P.bloodDeep, { ...S, ms: 480, n: 6, r: 1.6 }),
    shatter(140, P.omnia, { ...S, ms: 560, n: 12, r: 1.3 }),
    ...toll(P.crimson, 180, 2, "self"),
  ], 820),

  // ── RUIN · WITH THE SOUL ──────────────────────────────────────────
  // "Ruin 1 being AND its Soul." The Soul is the being's whole lineage
  // — every rung it climbed and every brand it was ever given — so this
  // is categorically worse than a ruin and there are only a handful of
  // cards in the game that can do it (Weeping Dagger, Necklace of
  // Congealed Death, Solomon Godfry's Manslaughter).
  //
  // The sever is what marks the difference: the lineage is CUT, and
  // then the being comes apart. Debris twice, because there is more of
  // it to lose.
  ruin_soul: score("Ruined · Soul and All", [
    sever(0, P.omnia, { ...S, ms: 560, r: 1.9, size: 0.048, size2: 0.34, tone: -20 }),
    crack(200, P.bloodDeep, { ...S, ms: 560, n: 8, r: 2.2 }),
    shatter(340, P.omnia, { ...S, ms: 620, n: 15, r: 1.6 }),
    motes(480, P.griefViolet, { ...S, ms: 520, n: 24, r: 1.6, size: 0.07 }),
    ...toll(P.crimson, 400, 3, "self"),
  ], 1120, { shake: SHAKE.hard(0) , surge: SURGE.full(P.omnia, 0) }),

// ═══════════════════════════════════════════════════════════════════
// THE ZONES — Graveshift · Draw · Necropolis
// ═══════════════════════════════════════════════════════════════════

  // ── GRAVESHIFT ────────────────────────────────────────────────────
  // The Necropolis-into-Grimoire mechanic, 10 Khao. An entire discard
  // pile going home — the single largest zone movement in the game and
  // one of the few genuinely reversible things about death in Terror.
  //
  // The vortex runs `out` because for once the cards are leaving the
  // ground rather than being pulled from it, and the grave marks go up
  // WITH them. It is the only score in the game where a grave is a
  // happy image.
  graveshift: score("Graveshift", [
    grave(0, P.griefViolet, { ...S, ms: 760, n: 8, r: 1.0, size: 24, tone: "☠" }),
    vortex(240, P.ritual, { ...S, ms: 720, r: 1.6, n: 60, spin: 6.0, size: 1.1, tone: "out" }),
    rift(700, P.returnGreen, { ...S, ms: 520, r: 1.6, size: 0.038, size2: 0.32 }),
    embers(760, P.returnCore, { ...S, ms: 560, n: 28, r: 0.85, size: 0.075, size2: 1.8 }),
    ...toll(P.ritual, 800, 2, "self"),
  ], 1420, { surge: SURGE.soft(P.ritual, 240) }),

  // ── THE DRAWING ───────────────────────────────────────────────────
  // "The Grimoire gives one up." H21's note is exact and this score
  // honours it: a draw happens several times a turn and must NEVER
  // demand attention. One small rift at the pile, nothing else, 320ms.
  //
  // It exists at all only so that drawing from the Luxmoire can look
  // different from drawing from the Grimoire (see below) — otherwise
  // the flight alone would have been the whole answer.
  draw: score("The Drawing", [
    rift(0, P.gold, { ...S, ms: 320, r: 0.85, size: 0.026, size2: 0.18 }),
  ], 320),

  // ── THE DRAWING · LUXMOIRE ────────────────────────────────────────
  // The side deck: Imperas, U'hltis, Affinities. Everything that comes
  // out of here is an ATTACHMENT, and attachments are the ladder — so
  // this one is gold-pale rather than gold and carries a single spark.
  // A very small tell, and after fifty matches it is one a player reads
  // without knowing they are reading it.
  draw_lux: score("The Drawing · Luxmoire", [
    rift(0, P.goldPale, { ...S, ms: 340, r: 0.9, size: 0.028, size2: 0.2 }),
    star(120, P.judgeGold, { ...S, ms: 280, r: 0.7, size: 0.035 }),
  ], 400),

  // ── THE DESCENT TO THE NECROPOLIS ─────────────────────────────────
  // "It broke where it stood." The card is already falling — H21's
  // DEPARTURE flight owns that motion — so this is only what is left
  // behind at the rift it LEFT. Deliberately drab: a descent is a debt.
  necropolis: score("The Descent", [
    motes(0, P.griefViolet, { ...S, ms: 520, n: 18, r: 1.2, size: 0.065 }),
    shock(80, P.griefViolet, { ...S, ms: 440, size: 0.16, r: 1.2, tone: 0.5 }),
  ], 560),

// ═══════════════════════════════════════════════════════════════════
// THE CLOCKS — the Turn, the Cycle, the Response Window
// ═══════════════════════════════════════════════════════════════════

  // ── THE TURN SWEEP ────────────────────────────────────────────────
  // The chair changes. A band of light crosses the felt from the old
  // seat to the new one — the board's way of saying whose it is now.
  // Seen more than any other score in the game (twice a Cycle, every
  // Cycle, forever), so it is 460ms of exactly one thing.
  turn: score("The Turn", [
    wave(0, P.vellum, { from: "self", to: "target", ms: 460, r: 7.2, size: 0.3, tone: 0.5 }),
  ], 460),

  // ── THE CYCLE ─────────────────────────────────────────────────────
  // One full rotation of the Shatterfield — both Aenarchists have
  // acted, and play returns to whoever started it. Rules that say "one
  // full Cycle" resolve HERE, and a player tracking a 2-turn ward needs
  // to see the boundary. Same sweep as the Turn with a ring at the
  // seam: the clock's minute hand versus its hour hand.
  cycle: score("The Cycle", [
    wave(0, P.goldPale, { from: "self", to: "target", ms: 520, r: 7.2, size: 0.36, tone: 0.6 }),
    rune(160, P.gold, { to: "seam", ms: 560, r: 1.4, n: 12, spin: 0.4 }),
    ...toll(P.goldPale, 320, 1, "seam"),
  ], 780),

  // ── THE RESPONSE WINDOW · OPEN ────────────────────────────────────
  // The interrupt opportunity during the adversary's turn — the moment
  // a player may answer with a Nihil ward, an Affinity, a counter.
  // Terror's most time-pressured moment.
  //
  // A rune INSCRIBES at the seam: something has been opened and it is
  // waiting for you. Deliberately not bright, not loud, and not on
  // anybody's half — the Window belongs to the table, not to a seat.
  window_open: score("The Response Window", [
    rune(0, P.indigo, { to: "seam", ms: 520, r: 1.6, n: 8, spin: 0.5 }),
    bloom(140, P.indigo, { to: "seam", ms: 320, r: 0.9, tone: 0.55 }),
  ], 520),

  // ── THE RESPONSE WINDOW · CLOSE ───────────────────────────────────
  // Nobody answered, or the chain resolved. The same ring, run shut.
  // One primitive, one flag, and the pair reads as a door.
  window_close: score("The Window Closes", [
    rune(0, P.indigo, { to: "seam", ms: 420, r: 1.6, n: 8, spin: 0.5, tone: "close" }),
  ], 420),

  // ── NULLIFIED ─────────────────────────────────────────────────────
  // The generic refusal, for every nullification that has no card of
  // its own to speak for it. Nihil's verb, at its smallest: a dome
  // snaps up, holds, and thins. "The effect is nullified; the fare
  // stays paid."
  nullify: score("Nullified", [
    veil(0, P.white, { ...S, ms: 460, r: 1.15, size: 0.7 }),
    star(80, P.starWhite, { ...S, ms: 320, r: 1.1, size: 0.04 }),
  ], 520),

// ═══════════════════════════════════════════════════════════════════
// THE CURRENCIES — Anima and Khao
// ═══════════════════════════════════════════════════════════════════
// Both are already shown as floating numbers at the seat plate, which
// is the honest, readable, unmissable answer and it is not being
// replaced. These add the FEEL underneath the number, and they are
// among the quietest scores in the game precisely because the number is
// doing the informing.

  // Anima gained. Warm, rising, and over in 480ms.
  anima_gain: score("Anima Rises", [
    embers(0, P.animaGreen, { ...S, ms: 480, n: 20, r: 0.6, size: 0.07, size2: 1.5 }),
  ], 480),

  // Anima lost. The inverse: motes falling INWARD and one ring. There
  // is no crack and no debris — a Warlock losing Anima is not a card
  // being damaged, and conflating the two would be a lie the player
  // eventually notices.
  anima_loss: score("Anima Dims", [
    motes(0, P.animaRed, { ...S, ms: 460, n: 22, r: 1.1, size: 0.07, tone: "in" }),
    shock(120, P.crimson, { ...S, ms: 420, size: 0.18, r: 1.5, tone: 0.6 }),
  ], 560),

  // ── THE BLEED ─────────────────────────────────────────────────────
  // A single hit of 8 or more Anima. Show Stomper does ten; Bloody Hell
  // wins the duel on 45 lost. At that size the quiet version is a lie,
  // so this is the one currency score with real weight — but it is
  // still under a second, because the number already said the number.
  anima_bleed: score("The Bleed", [
    motes(0, P.animaRed, { ...S, ms: 520, n: 40, r: 2.2, size: 0.08, tone: "in" }),
    crack(160, P.bloodDeep, { ...S, ms: 620, n: 8, r: 2.8 }),
    ...toll(P.crimson, 240, 3, "self"),
  ], 900, { shake: SHAKE.hard(160) , surge: SURGE.soft(P.crimson, 160) }),

  // Khao gained — the resource replenishing. Cool where Anima is warm,
  // which is the whole distinction and the only one needed.
  khao_gain: score("Khao Gathers", [
    embers(0, P.khaoBlue, { ...S, ms: 440, n: 16, r: 0.5, size: 0.065, size2: 1.2 }),
  ], 440),

  // Khao spent. A stream leaving toward the thing being paid for —
  // 300ms, and it is the shortest thing with a direction in it.
  khao_spend: score("Khao Spent", [
    stream(0, P.khaoBlue, { from: "self", to: "target", ms: 300, n: 22, r: 0.16, size: 0.05 }),
  ], 300),

// ═══════════════════════════════════════════════════════════════════
// SHADOW GAMBLING — the win and the loss
// ═══════════════════════════════════════════════════════════════════
// Nine Tomes, four Imperas, a U'hlti and an Affinity all resolve
// through Shadow Gambling, and Ace of Shades wins the entire duel on
// seven in a row. The coin has already been thrown by the card's own
// score by the time these fire — these are only the RESULT, and they
// are short because the drama was the hang, not the verdict.

  // Won. J'ynxi's green, one star, one ring.
  sg_win: score("The Gambling, Won", [
    star(0, P.jynxi, { ...S, ms: 420, r: 1.8, size: 0.055 }),
    ...toll(P.jynxi, 40, 2, "self"),
  ], 620),

  // Lost. The same beat, refused: the ring runs closed and there is no
  // star at all. "J'ynxi is watching, and she is delighted" — and she
  // is delighted either way, which is the joke this pair is telling.
  sg_loss: score("The Gambling, Lost", [
    rune(0, P.shadow, { ...S, ms: 480, r: 1.2, n: 6, spin: 0.8, tone: "close" }),
    motes(120, P.violetDeep, { ...S, ms: 400, n: 16, r: 1.0, size: 0.06, tone: "in" }),
  ], 560),

// ═══════════════════════════════════════════════════════════════════
// THE SOLREND — its start and its end
// ═══════════════════════════════════════════════════════════════════

  // ── THE OPENING ───────────────────────────────────────────────────
  // A duel begins. Rock, paper, scissors has decided who goes first,
  // three Esclings are down on each side, and the Shatterfield is live.
  // One ring inscribes across the whole table, from the seam outward.
  solrend_open: score("The Solrend Begins", [
    rune(0, P.vellum, { to: "seam", ms: 820, r: 3.2, n: 18, spin: 0.3 }),
    wave(300, P.goldPale, { from: "seam", to: "target", ms: 600, r: 7.4, size: 0.34, tone: 0.55 }),
    wave(340, P.goldPale, { from: "seam", to: "self", ms: 600, r: 7.4, size: 0.34, tone: 0.55 }),
    ...toll(P.vellum, 460, 2, "seam"),
  ], 1200, { surge: SURGE.soft(P.vellum, 0) }),

  // ── VICTORY ───────────────────────────────────────────────────────
  // Seen once per match at most, so it is allowed everything: the
  // column, the crown, and the mark. The warmest palette in the game.
  victory: score("Victory", [
    pillar(0, P.judgeGold, { ...S, ms: 820, r: 6.6, size: 0.54, spin: 0.35 }),
    spiral(180, P.goldPale, { ...S, ms: 760, tone: "up", spin: 2.6, r: 0.9, size: 3.4 }),
    embers(320, P.emberGold, { ...S, ms: 760, n: 40, r: 1.1, size: 0.08, size2: 2.4 }),
    halo(620, P.judgeGold, { ...S, ms: 860, r: 1.3, size: 0.95, spin: 1.0 }),
    star(700, P.starWhite, { ...S, ms: 520, r: 2.6, size: 0.065 }),
    ...toll(P.goldPale, 760, 3, "self"),
  ], 1720, { surge: SURGE.total(P.judgeGold, 0) }),

  // ── DEFEAT ────────────────────────────────────────────────────────
  // "Your Lyt will be extinguished if you fall in battle." The rulebook
  // opens with that warning, and this is it: everything the Victory
  // score does, inverted. The ring closes instead of opening, the light
  // converges instead of rising, and there is no crown at the end.
  //
  // No crack, no shatter, no debris. A Warlock losing is not a card
  // being destroyed — it is a light going out, and the score says
  // exactly that and then stops.
  defeat: score("Extinguished", [
    rune(0, P.shadow, { ...S, ms: 760, r: 2.4, n: 16, spin: 0.3, tone: "close" }),
    motes(200, P.griefViolet, { ...S, ms: 780, n: 48, r: 3.0, size: 0.08, tone: "in" }),
    veil(500, P.ink, { ...S, ms: 700, r: 2.6, size: 0.5 }),
    shock(620, P.violetDeep, { ...S, ms: 640, size: 0.42, r: 0.2, tone: 0.7 }),
  ], 1400, { shake: SHAKE.heavy(500) , surge: SURGE.total(P.shadow, 200) }),

  // ── A PROPHECY FULFILLED ──────────────────────────────────────────
  // The rarest ending in Terror: a duel won by a stated ambition rather
  // than by attrition. Eight cards can do it and most players will
  // never see one land.
  //
  // It quotes the Declaration exactly — the same pillar, the same slow
  // ring, the same crown — because the whole point is that the thing
  // announced at the start of the match has now come true. The player
  // should recognise the image. That recognition IS the payoff, and it
  // is why this is not simply Victory in indigo.
  prophecy_fulfilled: score("The Prophecy Is Fulfilled", [
    pillar(0, P.indigo, { ...S, ms: 880, r: 7.0, size: 0.46, spin: 0.15 }),
    rune(200, P.indigo, { ...S, ms: 940, r: 2.6, n: 22, spin: 0.2 }),
    glyph(560, P.indigo, { ...S, ms: 740, tone: "✧", size: 56, r: 0.85 }),
    halo(720, P.starWhite, { ...S, ms: 900, r: 1.4, size: 1.0, spin: 0.7 }),
    wave(820, P.indigo, { from: "self", to: "target", ms: 700, r: 7.6, size: 0.4, tone: 0.7 }),
    ...toll(P.indigo, 880, 3, "self"),
  ], 1900, { surge: SURGE.total(P.indigo, 200) }),
};

/**
 * Resolve a board verb. Total — an unknown key returns null and the
 * caller simply plays nothing, exactly like an unauthored card falls to
 * the taxonomy floor. Never throws; this runs one frame from a useFrame.
 */
export function boardScore(key) {
  if (!key) return null;
  return BOARD[key] || null;
}

/**
 * Pick the right ladder score for a rung. Small, but it is the one
 * place the "Lord is different" rule lives, and having it in a function
 * means no caller can forget it.
 */
export function ladderScore(fromLevel, toLevel, deviant) {
  if (deviant) return BOARD.deviance;
  if (!fromLevel || fromLevel === 0) return BOARD.transcend;
  if (toLevel >= 3) return BOARD.ascend_lord;
  return BOARD.ascend;
}

/**
 * ── DID THIS BEING CLIMB, OR IS IT A NEW BEING? ────────────────────
 *
 * From the renderer's point of view these are the same picture: one
 * card id replaced another in the same rift. Getting it wrong in either
 * direction is a real visual bug — a hard-won Ascend to Lord wearing a
 * plain Descent, or a fresh Beb being congratulated for a rung it never
 * climbed.
 *
 * THE TELL IS THE SOUL. A being that climbed leaves its lower self in
 * its own Soul stack — "a card's lineage, its history of what it
 * ascended from" — so the slot's soul GAINED exactly the card that used
 * to be standing there. A being merely swapped out does not do that.
 * No new Firebase field, no bus, and it resolves identically on both
 * clients because both diff the same two snapshots.
 *
 * This lives here, as a pure function, rather than inline in the
 * detector for one reason: inline it cannot be tested, and it is the
 * only piece of real INFERENCE in the whole animation system. Everything
 * else is a table lookup.
 *
 * Returns { climbed, deviant, from, to } — never throws, and `getCard`
 * is allowed to return null for anything.
 */
export function climbInfo(prevId, curId, prevSoul, curSoul, getCard) {
  const out = { climbed: false, deviant: false, from: 0, to: 1 };
  if (!prevId || !curId || prevId === curId) return out;
  const ps = Array.isArray(prevSoul) ? prevSoul : [];
  const cs = Array.isArray(curSoul) ? curSoul : [];
  // The lower self must have ARRIVED in the soul on this very diff.
  // Checking only "is it in the soul now" would fire every time a card
  // changed above an already-populated stack.
  if (cs.indexOf(prevId) === -1 || ps.indexOf(prevId) !== -1) return out;
  out.climbed = true;
  const a = (getCard && getCard(prevId)) || null;
  const b = (getCard && getCard(curId)) || null;
  out.from = (a && a.level) || 0;
  out.to = (b && b.level) || (out.from + 1);
  // DEVIANCE: school matches, Ilk does NOT. Permanent, and it locks the
  // being out of the U'hlti/Affinity ladder for the rest of the
  // Solrend. Both Ilks must be present to make the call — a missing
  // field is not evidence of divergence, and guessing here would paint
  // a legitimate Ascend with the sour score.
  if (a && b && a.ilk && b.ilk && a.ilk !== b.ilk) out.deviant = true;
  return out;
}

/** Anima delta → the right currency score. 8+ in one hit is a Bleed. */
export function animaScore(delta) {
  if (!delta) return null;
  if (delta > 0) return BOARD.anima_gain;
  return delta <= -8 ? BOARD.anima_bleed : BOARD.anima_loss;
}

export const describeBoard = () =>
  Object.keys(BOARD).map((k) => ({
    channel: "BOARD", key: k, name: BOARD[k].name, ms: BOARD[k].ms,
    cues: BOARD[k].cues.length,
  }));

const boardMotion = { BOARD, boardScore, ladderScore, animaScore, describeBoard };
export default boardMotion;
